import { Controller } from '@hotwired/stimulus';

// Formulaire de rapport guidé (shared/reports/_form) :
// - sommaire : état de chaque section (complète, points restants) et
//   compteur « N points sur M » des points obligatoires (data-required ;
//   data-depends : seulement la case de ce nom cochée ; data-rule="integer" :
//   entier d'au moins 1) ;
// - « Tout NA » d'une section ou d'un bloc (data-na-scope) : NA sur ses
//   points encore vides, défait par « Annuler » (points restés à NA remis à
//   vide) ;
// - blocs qui dépendent d'un champ (cible dependent : data-when-name, nom
//   d'un groupe de boutons radio, d'une liste ou d'une case ;
//   data-when-values), masqués hors de ces valeurs et vidés, sauf
//   data-when-keep (champs gardés) ;
// - taille des fichiers choisis dans leur champ caché ;
// - remplacement replié : remis à « Aucune » ;
// - relecture (fenêtre) : points NOK (états de la bouteille au-delà de leur
//   seuil compris), points manquants ou invalides, verdict
//   (point data-verdict), avertissements de la page (data-review-notice
//   visibles), dates ; « Enregistrer le rapport » permis seulement complet,
//   valide et confirmé ; les points manquants sont signalés (ambre,
//   aria-invalid) dès la première relecture ;
// - clavier sur un point qui a le focus (bouton radio) : O (OK, OUI),
//   N (NOK, NON), A (NA, s'il est proposé), Entrée (point suivant non
//   renseigné) ; flèches natives ;
// - téléphone (sous 640 px) : bouton « Section N / M · K points restants »
//   (section arrivée sous le sommaire, points obligatoires restants du
//   rapport) qui déplie la liste des sections, refermée au choix d'une
//   section, par Échap ou un clic ailleurs.
// Le serveur vérifie les mêmes points obligatoires à l'enregistrement
// (ReportRequiredFields).
export default class extends Controller {
    static targets = ['section', 'point', 'field', 'outlineItem', 'counter', 'nokList', 'missingList', 'verdict', 'dates', 'sign', 'save', 'hint',
        'dependent', 'notices', 'outlineToggle', 'outlineList', 'outlineSummary'];
    static outlets = ['dialog'];

    connect() {
        this.attempted = false;
        this.naFilled = new Map();
        this.syncDependents();
        this.refresh();
    }

    changed({ target }) {
        if (target.type === 'file') this.recordSize(target);
        this.syncDependents();
        this.refresh();
    }

    // Points et champs obligatoires visibles, dans l'ordre de la page.
    get required() {
        return this.inOrder([...this.pointTargets, ...this.fieldTargets].filter((item) => item.dataset.required === 'true' && this.visible(item)
            && this.dependencyMet(item)));
    }

    get missing() {
        return this.required.filter((item) => !this.filled(item));
    }

    // Champs visibles dont la saisie est refusée par le navigateur (nombre
    // hors bornes ou illisible).
    get invalid() {
        const missing = this.missing;
        return this.fieldTargets.filter((item) => this.visible(item) && !missing.includes(item)
            && [...item.querySelectorAll('input:not([type="hidden"]), textarea')].some((input) => !input.checkValidity()));
    }

    get complete() {
        return this.missing.length === 0 && this.invalid.length === 0 && this.hasSignTarget && this.signTarget.checked;
    }

    inOrder(items) {
        return items.sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));
    }

    visible(item) {
        return !item.closest('[hidden]');
    }

    // Règle conditionnelle : case de ce nom cochée.
    dependencyMet(item) {
        const name = item.dataset.depends;
        if (!name) return true;
        return Boolean(this.element.querySelector(`input[type="checkbox"][name="${CSS.escape(name)}"]:checked`));
    }

    filled(item) {
        if (this.pointTargets.includes(item)) return this.checked(item) !== null;

        const input = item.querySelector('[data-report-form-value]');
        if (input.type === 'file') {
            const kept = [...item.querySelectorAll('[data-attached]')].some((box) => !box.checked);
            return input.files.length > 0 || kept;
        }
        if (item.dataset.rule === 'integer') return /^[1-9]\d*$/.test(input.value.trim());
        return input.value.trim() !== '';
    }

    checked(point) {
        return point.querySelector('input[type="radio"]:checked');
    }

    refresh() {
        const required = this.required;
        const missing = required.filter((item) => !this.filled(item));
        const invalid = this.invalid;
        const done = required.length - missing.length;
        this.counterTargets.forEach((counter) => {
            counter.textContent = `${done} point${done > 1 ? 's' : ''} sur ${required.length}`;
        });
        this.outlineItemTargets.forEach((link) => this.renderOutlineItem(link, required, missing));
        this.left = missing.length;
        this.renderOutlineSummary();
        const flagged = this.attempted ? [...missing, ...invalid] : [];
        this.pointTargets.concat(this.fieldTargets).forEach((item) => this.mark(item, flagged.includes(item)));
        if (this.hasSaveTarget) this.saveTarget.disabled = !this.complete;
        if (this.hasHintTarget) this.hintTarget.textContent = this.hint(missing, invalid);
    }

    renderOutlineItem(link, required, missing) {
        const section = this.sectionTargets.find((target) => target.id === link.dataset.section);
        const total = required.filter((item) => section?.contains(item)).length;
        const left = missing.filter((item) => section?.contains(item)).length;
        let state = 'none';
        if (total > 0) state = left === 0 ? 'complete' : (left === total ? 'empty' : 'partial');
        link.dataset.state = state;
        link.querySelector('[data-count]').textContent = left > 0 ? `(${left})` : '';
        link.querySelector('[data-status]').textContent = { complete: ' : complète', none: '' }[state] ?? ` : ${left} à renseigner`;
    }

    // Point ou champ manquant après une relecture : ambre, aria-invalid.
    mark(item, invalid) {
        if (this.pointTargets.includes(item)) {
            if (invalid) item.setAttribute('aria-invalid', 'true');
            else item.removeAttribute('aria-invalid');
            return;
        }
        item.querySelectorAll('input:not([type="hidden"]), textarea, select').forEach((input) => {
            if (input.type === 'checkbox') return;
            if (invalid) input.setAttribute('aria-invalid', 'true');
            else if (!input.closest('.form-field')?.querySelector('[id$="_error"]:not([hidden])')) input.setAttribute('aria-invalid', 'false');
        });
    }

    hint(missing, invalid) {
        if (missing.length > 0) return `Encore ${missing.length} point${missing.length > 1 ? 's' : ''} à renseigner avant l’enregistrement.`;
        if (invalid.length > 0) return `${invalid.length} valeur${invalid.length > 1 ? 's' : ''} à corriger avant l’enregistrement.`;
        return this.hasSignTarget && this.signTarget.checked ? '' : 'Cochez la confirmation pour enregistrer le rapport.';
    }

    // « Tout NA » / « Annuler » d'une section ou d'un bloc.
    allNa({ currentTarget: button }) {
        const section = button.closest('[data-na-scope]') || button.closest('[data-report-form-target~="section"]');
        if (button.getAttribute('aria-pressed') === 'true') {
            (this.naFilled.get(section) || []).forEach((radio) => { radio.checked = false; });
            this.naFilled.delete(section);
            this.toggleNaButton(button, false);
        } else {
            const radios = this.pointTargets.filter((point) => section.contains(point) && this.checked(point) === null)
                .map((point) => point.querySelector('input[type="radio"][value="na"]')).filter(Boolean);
            radios.forEach((radio) => { radio.checked = true; });
            this.naFilled.set(section, radios);
            this.toggleNaButton(button, true);
        }
        this.form.dispatchEvent(new Event('change', { bubbles: true }));
    }

    toggleNaButton(button, pressed) {
        button.setAttribute('aria-pressed', String(pressed));
        button.textContent = pressed ? button.dataset.labelOn : button.dataset.labelOff;
    }

    get form() {
        return this.element.querySelector('form#report-form');
    }

    syncDependents() {
        this.dependentTargets.forEach((block) => {
            const shown = JSON.parse(block.dataset.whenValues).includes(this.fieldValue(block.dataset.whenName));
            block.hidden = !shown;
            if (shown || 'whenKeep' in block.dataset) return;
            block.querySelectorAll('input, textarea').forEach((input) => {
                if (['checkbox', 'radio'].includes(input.type)) input.checked = false;
                else input.value = '';
            });
        });
    }

    // Valeur envoyée par le champ de ce nom : bouton radio coché, liste, case
    // cochée (vide sinon).
    fieldValue(name) {
        const fields = [...this.element.querySelectorAll(`[name="${CSS.escape(name)}"]`)].filter((field) => field.type !== 'hidden');
        const field = fields.find((item) => !['radio', 'checkbox'].includes(item.type) || item.checked);
        return field?.value ?? '';
    }

    recordSize(input) {
        const field = input.dataset.sizeField && this.element.querySelector(`#${CSS.escape(input.dataset.sizeField)}`);
        if (field) field.value = [...input.files].reduce((total, file) => total + file.size, 0) || '';
    }

    replacement({ currentTarget: details }) {
        if (details.open) return;
        const select = details.querySelector('select');
        if (!select || select.value === '0') return;
        select.value = '0';
        select.closest('[data-controller~="combobox"]')?.dispatchEvent(new CustomEvent('combobox:render'));
        select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Envoi du rapport refusé tant qu'il n'est pas complet et confirmé (le
    // brouillon part toujours).
    guard(event) {
        if (event.submitter !== this.saveTarget || this.complete) return;
        event.preventDefault();
        event.stopImmediatePropagation();
    }

    review() {
        this.attempted = true;
        this.refresh();
        const visible = this.pointTargets.filter((point) => !point.closest('[hidden]'));
        const nok = visible.filter((point) => this.nok(point));
        this.fillList(this.nokListTarget, nok.map((point) => [point, `${point.dataset.label} : ${this.checked(point).dataset.text}`]),
            'Aucun point non conforme.');
        this.fillList(this.missingListTarget, [...this.missing.map((item) => [item, item.dataset.label]),
            ...this.invalid.map((item) => [item, `${item.dataset.label} : valeur incorrecte`])], 'Tous les points obligatoires sont renseignés.');
        const verdict = visible.find((point) => point.dataset.verdict === 'true');
        const choice = verdict && this.checked(verdict);
        this.verdictTarget.textContent = choice
            ? { true: 'conforme (OUI)', yes: 'conforme (OUI)', false: 'non conforme (NON)', no: 'non conforme (NON)' }[choice.value] : 'non renseigné';
        this.renderNotices();
        this.renderDates();
        this.dialogOutlet.open();
    }

    // Point non conforme : NOK (NON), ou état de la bouteille au-delà de son
    // seuil (data-tank-state-nok-above-value).
    nok(point) {
        const value = this.checked(point)?.value;
        if (value === undefined) return false;
        const above = point.dataset.tankStateNokAboveValue;
        return above === undefined ? value === 'no' : Number(value) > Number(above);
    }

    fillList(list, entries, empty) {
        list.replaceChildren(...(entries.length ? entries : [[null, empty]]).map(([item, text]) => {
            const li = document.createElement('li');
            if (!item) {
                li.className = 'text-muted';
                li.textContent = text;
                return li;
            }
            const link = document.createElement('a');
            link.href = `#${item.id || item.querySelector('[id]').id}`;
            link.className = 'text-accent underline-offset-2 hover:underline';
            link.dataset.action = 'report-form#jump';
            link.textContent = text;
            const section = item.closest('[data-report-form-target~="section"]');
            li.append(link);
            if (section) li.append(` (${section.dataset.title})`);
            return li;
        }));
    }

    // Avertissements visibles de la page (data-review-notice), repris dans la
    // relecture.
    renderNotices() {
        if (!this.hasNoticesTarget) return;
        const notices = [...this.element.querySelectorAll('[data-review-notice]')].filter((notice) => !notice.hidden && this.visible(notice));
        this.noticesTarget.replaceChildren(...notices.map((notice) => {
            const copy = document.createElement('p');
            copy.className = 'rounded-md border border-due/30 bg-due-soft px-3 py-2 text-due';
            copy.textContent = notice.textContent.trim();
            return copy;
        }));
    }

    renderDates() {
        const dates = [...this.element.querySelectorAll('[data-report-form-date]')].filter((input) => !input.closest('[hidden]'));
        const rows = dates.flatMap((input) => {
            const term = document.createElement('dt');
            term.className = 'text-muted';
            term.textContent = input.dataset.reportFormDate;
            const value = document.createElement('dd');
            value.textContent = input.value || `vide : ${input.dataset.empty}`;
            return [term, value];
        });
        this.datesTarget.replaceChildren(...rows);
    }

    // Lien du sommaire ou de la relecture : fenêtre et liste du sommaire
    // fermées, point montré et pris au clavier.
    jump(event) {
        const target = document.getElementById(event.currentTarget.getAttribute('href').slice(1));
        if (!target) return;
        event.preventDefault();
        if (this.hasDialogOutlet) this.dialogOutlet.close();
        this.closeOutline();
        const section = target.matches('section');
        if (section) this.jumped = this.outlineItemTargets.findIndex((link) => link.dataset.section === target.id);
        target.scrollIntoView({ block: section ? 'start' : 'center' });
        if (section) target.setAttribute('tabindex', '-1');
        const fields = [...target.querySelectorAll('input:not([type="hidden"]), textarea, select, button')].filter((field) => field.checkVisibility());
        const focusable = section || target.matches('input, textarea, select') ? target : fields[0];
        focusable?.focus({ preventScroll: true });
    }

    // Raccourcis d'un point de contrôle qui a le focus (jamais dans un champ
    // texte : seulement sur ses boutons radio).
    shortcut(event) {
        const radio = event.target;
        if (radio.type !== 'radio' || event.ctrlKey || event.metaKey || event.altKey || event.isComposing) return;
        const point = this.pointTargets.find((item) => item.contains(radio));
        if (!point) return;

        if (event.key === 'Enter') {
            event.preventDefault();
            this.focusPoint(this.nextEmptyPoint(point));
            return;
        }
        const values = { o: ['yes', 'true'], n: ['no', 'false'], a: ['na'] }[event.key.toLowerCase()];
        const choice = values && [...point.querySelectorAll('input[type="radio"]')].find((input) => values.includes(input.value));
        if (!choice) return;
        event.preventDefault();
        if (!choice.checked) choice.click();
        choice.focus();
    }

    // Point visible non renseigné après `point`, sinon le premier avant lui.
    nextEmptyPoint(point) {
        const empty = this.pointTargets.filter((item) => item !== point && this.visible(item) && this.checked(item) === null);
        return empty.find((item) => point.compareDocumentPosition(item) & Node.DOCUMENT_POSITION_FOLLOWING) || empty[0];
    }

    focusPoint(point) {
        if (!point) return;
        point.scrollIntoView({ block: 'center' });
        point.querySelector('input[type="radio"]')?.focus({ preventScroll: true });
    }

    toggleOutline() {
        if (!this.hasOutlineListTarget) return;
        this.setOutline(!this.outlineListTarget.hasAttribute('data-open'));
    }

    closeOutline() {
        if (this.hasOutlineListTarget && this.outlineListTarget.hasAttribute('data-open')) this.setOutline(false);
    }

    outsideOutline({ target }) {
        if (this.hasOutlineToggleTarget && !this.outlineToggleTarget.closest('nav').contains(target)) this.closeOutline();
    }

    setOutline(open) {
        this.outlineListTarget.toggleAttribute('data-open', open);
        this.outlineToggleTarget.setAttribute('aria-expanded', String(open));
    }

    // Défilement : section à l'écran du bouton du sommaire (une fois par
    // image).
    track() {
        if (this.tracking) return;
        this.tracking = true;
        requestAnimationFrame(() => {
            this.tracking = false;
            this.renderOutlineSummary();
        });
    }

    renderOutlineSummary() {
        if (!this.hasOutlineSummaryTarget || !this.outlineToggleTarget.checkVisibility()) return;
        const items = this.outlineItemTargets;
        const left = this.left ?? 0;
        const plural = left > 1 ? 's' : '';
        this.outlineSummaryTarget.textContent = `Section ${this.currentSection(items) + 1} / ${items.length} · ${left} point${plural} restant${plural}`;
    }

    // Dernière section arrivée sous le sommaire (à sa marge de défilement,
    // comme après un saut) ; en bas de page, au moins la dernière section
    // choisie dans le sommaire.
    currentSection(items) {
        const bottom = this.outlineToggleTarget.closest('nav').getBoundingClientRect().bottom;
        let current = 0;
        items.forEach((link, index) => {
            const section = document.getElementById(link.dataset.section);
            if (!section || !this.visible(section)) return;
            const line = Math.max(bottom, parseFloat(getComputedStyle(section).scrollMarginTop) || 0) + 1;
            if (section.getBoundingClientRect().top <= line) current = index;
        });
        const end = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
        return end && this.jumped !== undefined ? Math.max(current, this.jumped) : current;
    }
}
